import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { useContext, useState } from 'react'
import { MEMBERS } from '../../../constants/chatRooms'
import { AuthenticationContext } from '../../../context/authenticationContext'
import { useQuestions } from '../../../hooks/useQuestions'
import { useViewportHeight } from '../../../hooks/useViewportHeight'
import { sortQuestion } from '../../../utils/APIs/publicMessages'
import { toastError } from '../../../utils/toast'
import { setPageTitle } from '../../../utils/utilities'
import MessageList from '../../Messages/MessageList'
import Tooltip from '../../Utilities/Tooltip'
import ClearQuestionsModal from './ClearQuestionsModal'
import Question from './Question'

const QuestionAndAnswer = () => {
  useViewportHeight()
  setPageTitle('Question and Answer')
  const { user } = useContext(AuthenticationContext)

  const {
    questions,
    setQuestions,
    getAllQuestions,
    removeAllQuestions,
    removeQuestion
  } = useQuestions({ user })

  const [clearQuestionsModal, setClearQuestionsModal] = useState(false)

  const handleDragEnd = async (event) => {
    if (event.canceled) return

    const { source } = event.operation
    if (isSortable(source)) {
      const { initialIndex, index } = source
      if (initialIndex !== index) {
        // DragEnd event only changes the DOM
        // Re-arrange question within component state to match the DOM
        let newQuestions = [...questions]
        newQuestions.splice(index, 0, newQuestions.splice(initialIndex, 1)[0])

        setQuestions(() => {
          return newQuestions
        })

        // Request database change to reflect local state
        try {
          if (index === 0) {
            await sortQuestion({
              question: newQuestions[index]._id,
              after: null,
              before: newQuestions[index + 1]._id
            })
          } else if (index === questions.length - 1) {
            await sortQuestion({
              question: newQuestions[index]._id,
              after: newQuestions[index - 1]._id,
              before: null
            })
          } else {
            await sortQuestion({
              question: newQuestions[index]._id,
              after: newQuestions[index - 1]._id,
              before: newQuestions[index + 1]._id
            })
          }
        } catch (error) {
          // If database change is rejected, fetch fresh state
          await getAllQuestions()
          toastError(
            'There was an issue processing your request. Please try again.'
          )
        }
      }
    }
  }

  return (
    <div className="grid grid-cols-2 grid-rows-[1fr-auto] grid-flow-col h-full w-full gap-2">
      <div className="row-span-2 flex h-full w-full flex-col gap-2 overflow-auto-y">
        <div className="flex gap-3 justify-between">
          <div className="flex gap-3 items-center">
            <h2>Q&A</h2>
            <Tooltip>
              Add public messages to this list to be addressed by the host.
              Rearrange the order of the messages in the list to prioritize
              which questions are answered first.
            </Tooltip>
          </div>
          <button
            className="button-cancel"
            type="button"
            aria-label="Clear All Questions"
            onClick={() => {
              setClearQuestionsModal(true)
            }}
          >
            Clear All
          </button>
        </div>
        {questions.length > 0 ? (
          <ol className="h-full w-full overflow-y-auto flex flex-col gap-1.5">
            <DragDropProvider onDragEnd={handleDragEnd}>
              {questions.map((question, index) => (
                <Question
                  key={question._id}
                  question={question}
                  index={index}
                  handleRemove={removeQuestion}
                />
              ))}
            </DragDropProvider>
          </ol>
        ) : (
          <div className="mb-2 p-4 flex content-center justify-center bg-neutral-100 py-2 shadow dark:bg-neutral-800">
            <p>
              Add messages to this list by clicking the wrench icon then "Add to
              Q&A".
            </p>
          </div>
        )}
      </div>
      <MessageList
        channel={MEMBERS}
        modView={true}
        showShadowBanned={false}
        emphasizeQuestions={true}
      />
      <ClearQuestionsModal
        open={clearQuestionsModal}
        setOpen={setClearQuestionsModal}
        clearQuestions={removeAllQuestions}
      />
    </div>
  )
}

export default QuestionAndAnswer
